import { Capacitor } from '@capacitor/core';
import { ExoraShortcuts } from './capacitorShortcuts';

const SHORTCUT_ROUTES: Record<string, string> = {
  searchHotels: '/stays?openSearch=true',
  myBookings: '/account?tab=reservations',
  savedHotels: '/account?tab=favourites',
  // AppDelegate EXORA_SHORTCUT event types
  search: '/stays?openSearch=true',
  bookings: '/account?tab=reservations',
  saved: '/account?tab=favourites',
};

const SHORTCUT_EVENT_NAME = 'cap-shortcut';

let isInitialized = false;
let isRouterReady = false;
let pendingRoute: string | null = null;
let lastHandledType: string | null = null;
let lastHandledAt = 0;

function normalizeShortcutType(rawValue: string): string {
  return rawValue.replace(/^.*:\/\//, '').replace(/[/?#].*$/, '').trim();
}

function shouldIgnoreDuplicate(type: string): boolean {
  const now = Date.now();
  if (lastHandledType === type && now - lastHandledAt < 1500) {
    console.log(`[Shortcuts] ignoring duplicate shortcut | type=${type}`);
    return true;
  }

  lastHandledType = type;
  lastHandledAt = now;
  return false;
}

function dispatchRoute(route: string): void {
  // Save destination for auth redirect recovery (quick actions may fire before auth loads)
  if (route.startsWith('/account')) {
    try { sessionStorage.setItem('exora_quick_action_return', route); } catch {}
  }
  window.dispatchEvent(new CustomEvent<string>(SHORTCUT_EVENT_NAME, { detail: route }));
}

function queueOrDispatchRoute(route: string, source: string): void {
  if (isRouterReady) {
    console.log(`[Capacitor] Navigating to: ${route} | source=${source}`);
    dispatchRoute(route);
    return;
  }

  pendingRoute = route;
  console.log(`[Shortcuts] web app not ready, queued route ${route} | source=${source}`);
}

function handleShortcutType(rawType: string, source: string): void {
  const type = normalizeShortcutType(rawType);
  if (!type) {
    console.warn(`[Shortcuts] empty type after normalize | raw=${rawType}`);
    return;
  }

  console.log(`[Shortcuts] tapped ${type} | source=${source} | routerReady=${isRouterReady}`);

  if (shouldIgnoreDuplicate(type)) return;

  const route = SHORTCUT_ROUTES[type];
  if (!route) {
    console.warn(`[Shortcuts] no route mapped for "${type}" | available keys: ${Object.keys(SHORTCUT_ROUTES).join(', ')}`);
    return;
  }

  console.log(`[Shortcuts] resolved route=${route} for type=${type}`);
  queueOrDispatchRoute(route, source);
}

/** Handle EXORA_SHORTCUT events from iOS AppDelegate */
export function handleExoraShortcut(type: string): void {
  handleShortcutType(type, 'EXORA_SHORTCUT');
}

async function consumePendingShortcut(source: string): Promise<void> {
  try {
    const result = await ExoraShortcuts.consumePendingShortcut();
    if (!result?.type) return;

    console.log(`[Shortcuts] pending shortcut received | type=${result.type} | source=${result.source ?? source}`);
    handleShortcutType(result.type, result.source ?? source);
  } catch (e) {
    console.warn('[Shortcuts] Failed to consume pending shortcut', e);
  }
}

export async function markQuickActionsReady(): Promise<void> {
  isRouterReady = true;

  // If initQuickActions already queued a route, dispatch it immediately
  if (pendingRoute) {
    const route = pendingRoute;
    pendingRoute = null;
    console.log(`[Shortcuts] route consumed by router | ${route}`);
    dispatchRoute(route);
    return;
  }

  // Race condition fix: if consumePendingShortcut hasn't resolved yet
  // (initCapacitorPlugins is async, called without await), try consuming now
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await ExoraShortcuts.consumePendingShortcut();
      if (result?.type) {
        console.log(`[Shortcuts] late-consumed pending shortcut | type=${result.type}`);
        handleShortcutType(result.type, result.source ?? 'markReady');
      }
    } catch (e) {
      console.warn('[Shortcuts] markReady consumePending failed', e);
    }
  }
}

/**
 * Initialise iOS Home Screen Quick Action handling.
 * Native shortcuts are queued until the router is mounted, then dispatched.
 */
export function initQuickActions(): void {
  if (!Capacitor.isNativePlatform() || isInitialized) return;
  isInitialized = true;

  import('@capacitor/app')
    .then(({ App }) => {
      ExoraShortcuts.addListener('shortcutTriggered', ({ type, source }) => {
        if (!type) return;
        handleShortcutType(type, source ?? 'native event');
      }).catch((e) => {
        console.warn('[Shortcuts] Failed to attach native shortcut listener', e);
      });

      App.addListener('appUrlOpen', ({ url }) => {
        console.log(`[Shortcuts] appUrlOpen received | ${url}`);
        handleShortcutType(url, 'appUrlOpen');
      });

      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) return;
        void consumePendingShortcut('appStateChange');
      });

      App.getLaunchUrl().then((result) => {
        if (!result?.url) return;
        console.log(`[Shortcuts] launch URL detected | ${result.url}`);
        handleShortcutType(result.url, 'getLaunchUrl');
      });

      void consumePendingShortcut('startup');
      console.log('[Shortcuts] Quick Actions listener registered');
    })
    .catch((e) => {
      console.warn('[Shortcuts] Failed to initialise Quick Actions', e);
    });
}
